import { buildTrackingUrl } from '@apm/contracts';
import type { Tv2mEventType } from '@apm/contracts';
import type { Tv2mEventDetail } from '@apm/tv2m-client-sdk';

import { getNativeBridge } from './native/bridge.js';

/**
 * 외부 판매처 이탈 (B10).
 *
 * ── BLOOM 을 떠나는 유일한 순간이다 ─────────────────────────
 *
 * 절대 규칙 14 는 "어떤 tier 에서도 재생을 멈추지 않는다" 이고, B9 이 Tier 0 의
 * `location.href` 를 지우면서 그 규칙이 웹에서 성립했다. 남은 이탈은 하나뿐이다 —
 * **시청자가 외부 판매처를 직접 고른 결과**(`CLAUDE.md` 규칙 14 의 마지막 문단).
 * 그래서 이 파일이 앱에서 유일하게 나가는 문이고, 나가기 전에 해야 할 일을 여기 모은다.
 *
 * ── 순서가 곧 계약이다 ──────────────────────────────────────
 *
 * ```
 * 1. external_checkout_opened 를 낸다
 * 2. **보낼 때까지 기다린다**          ← 떠난 뒤에는 보낼 기회가 없다
 * 3. 추적 파라미터를 붙여 새 탭으로 연다
 * 4. 팝업이 막혔으면 같은 탭으로 간다
 * ```
 *
 * 2번이 3번보다 먼저인 이유: 4번(같은 탭 이동)이 일어나면 이 페이지는 그 자리에서
 * 죽는다. 큐에 남은 이벤트는 `pagehide` 가 한 번 더 밀어 보지만(`useTv2m`) 그것은
 * 최선 노력이고, **이 한 건은 이 시스템이 재려는 사업 지표 그 자체다** — "TV 화면에서
 * 본 상품이 실제 구매로 이어졌는가"의 마지막 관측점이 여기다.
 *
 * 대가로 **팝업 차단에 걸리기 쉬워진다.** `await` 를 하나라도 지나면 사용자 제스처
 * 컨텍스트가 끊기는 브라우저가 있고, 그때 `window.open` 은 `null` 을 준다. 그것을
 * 실패로 다루지 않고 같은 탭 이동으로 받는 것이 4번이다 — 기록은 이미 서버에 있으므로
 * 잃는 것은 "돌아올 탭" 뿐이다.
 */

/** 이벤트를 내고 보내는 두 통로. `Tv2mApi` 가 이 모양을 만족한다. */
export interface CheckoutDeps {
  trackEvent: (type: Tv2mEventType, detail?: Tv2mEventDetail) => void;
  flushEvents: () => Promise<void>;
}

/**
 * 추적 4파라미터 중 이 앱이 아는 둘 (절대 규칙 8).
 *
 * **둘 다 시청 세션에서 온다.** 통합 앱이라 같은 페이지의 시청 화면이 값을 들고 있고
 * (`useReceiver` 의 `view`), 그것을 상품 화면까지 prop 으로 내린다.
 *
 * Push·QR 로 들어와 시청 세션이 없으면 `deviceId` 는 빈 문자열이고 `playlistId` 는
 * `null` 이다. **그 경우 추적은 규칙 8 의 다른 경로가 맡는다** —
 * `sessionToken → match_id → MatchSession(receiverDeviceId, playlistId, productId)`
 * 서버측 역참조이고, 그것이 TV2M 경로의 QR 에 네 파라미터가 없는 이유이기도 하다.
 * 값이 없는데 있는 척 만들어 붙이면 **두 경로가 서로 다른 답을 주는** 상태가 된다.
 */
export interface CheckoutTracking {
  /** BLOOM Server `deviceId`(`dev-…`). 시청 세션이 없으면 빈 문자열 */
  deviceId: string;
  /** 이 시청 세션의 플레이리스트 상관관계 키. 세션이 없으면 `null` */
  playlistId: string | null;
}

export interface CheckoutTarget {
  /** 판매처 주소. BLOOM Server 는 `Product.deepLink`, 광고 경로(B11)는 `AdCreative.landingUrl` */
  deepLink: string;
  productId: string;
  /** 이 이탈을 매칭에 잇는 열쇠. 낙관적으로 연 시트에서는 아직 없을 수 있다 */
  matchId?: string;
  tracking: CheckoutTracking;
}

/**
 * 어디로 나갔는가. 화면이 이 값으로 안내를 고르고, **`params.exit` 으로 기록에도 남는다.**
 *
 * ★ **셋이 된 이유** (v21/F13). 설치된 앱(홈 화면 PWA · WebAPK)에서는 **`window.open` 이
 * 새 창을 주지 않는다** — 활성화가 남아 있어도 그렇다. 그때 예전에는 같은 탭으로 갔고,
 * 그것이 **방송이 있던 앱을 대체**해 돌아오면 진입 게이트였다. 이제 그 자리에 **진짜
 * 앵커 클릭**이 들어간다.
 *
 * - `new_tab` — 셸 브리지나 `window.open` 이 창을 줬다. **관측된 성공이다**
 * - `handed_off` — `<a target="_blank">` 를 눌러 **브라우저에 넘겼다.** 열렸는지는
 *   원리적으로 알 수 없다 — 그래서 화면 문구도 「열었습니다」라고 말하지 않는다
 * - `same_tab` — 앵커조차 만들 수 없었다(문서가 없다). 이 페이지는 죽는다
 */
export type CheckoutExit = 'new_tab' | 'handed_off' | 'same_tab';

/**
 * 전송을 기다리는 상한(ms).
 *
 * `flushEvents()` 는 절대 reject 하지 않지만(`Tv2mEventQueue`) **끝나지 않을 수는 있다** —
 * `FetchNetworkAdapter` 에 타임아웃이 없어서 서버가 응답을 붙들고 있으면 그대로 매달린다.
 * 그 상태에서 기다리기만 하면 시청자는 [판매처로 이동] 을 눌렀는데 아무 일도 일어나지
 * 않는 화면을 본다. **기록보다 이탈이 우선이다** — 기록을 못 남기는 것은 지표 한 건을
 * 잃는 것이고, 못 나가는 것은 구매를 잃는 것이다.
 *
 * 1.5초는 `requestPurchaseHandoff` 의 3초보다 짧다. 저쪽은 기다려야 화면을 그릴 수
 * 있지만 이쪽은 기다리지 않아도 갈 곳이 정해져 있다.
 *
 * **팝업이 이 상한 안에서 살아 있다.** 브라우저의 일시적 사용자 활성화(transient user
 * activation)는 클릭 뒤 몇 초간 유지되고, 그 안에서 부른 `window.open` 은 차단되지
 * 않는다 — 상한을 그보다 길게 잡으면 아래 폴백이 정상 경로가 된다. 실기에서 새 탭으로
 * 열렸다.
 */
const FLUSH_TIMEOUT_MS = 1_500;

/**
 * 추적 파라미터를 붙인 판매처 주소.
 *
 * **`buildTrackingUrl` 을 쓴다** (절대 규칙 8). 파라미터 이름을 여기서 다시 적으면
 * 서버의 랜딩 페이지(`GET /p/{id}`)·QR(`qr.ts`)과 출처가 갈리고, 어긋나도 화면에는
 * 드러나지 않은 채 **집계에서만** 갈린다.
 *
 * 순수 함수라 node 환경에서 검증된다 — 이 URL 이 곧 사업 지표이므로 브라우저 없이
 * 무는 검사가 있어야 한다 (`qr.test.ts` 와 같은 자리).
 */
export function buildCheckoutUrl(target: CheckoutTarget): string {
  return buildTrackingUrl(target.deepLink, {
    deviceId: target.tracking.deviceId,
    productId: target.productId,
    // 빈 문자열로 내려보낸다. `pl=` 자리가 사라지면 랜딩 로그의 모양이 경로마다 달라진다.
    playlistId: target.tracking.playlistId ?? '',
  });
}

/**
 * ★ **여기가 나갈 수 있는 주소인가** (v21/F13). 순수 함수라 브라우저 없이 검증된다.
 *
 * ── 왜 검사가 필요한가 ─────────────────────────────────────
 *
 * **빈 판매처 주소는 오류가 아니라 규칙이다** — 일반 채널 회차의 `deepLink` 는 빈
 * 문자열이고(계약을 nullable 로 바꾸지 않기로 한 결과), 그 회차에는 살 것이 없다.
 * 그런데 `buildCheckoutUrl('')` 은 던지지 않는다 — **`?src=fast&d=…` 라는 상대 URL** 을
 * 준다. 그리고 설치된 앱에서 상대 URL 은 **자기 오리진**이라 브라우저가 아니라 **앱
 * 자신이** 그리로 가고, 시청자가 보는 것은 **자기 앱의 시작 게이트**다.
 *
 * 실기에서 정확히 그렇게 났다(광고 시트가 회차를 열고 있었다 — `handoff.ts` 의
 * `AdSheetTarget`). 그 뿌리는 고쳤지만 **같은 종류의 침묵이 다시 오는 자리가 넷 있다** —
 * 일반 채널 시트 · 소재의 `landingUrl` 이 비어 등록된 경우 · 상대 경로 딥링크 ·
 * `javascript:` 같은 스킴. 그래서 뿌리와 별개로 **문 앞에서 한 번 더 본다.**
 *
 * ── ★ v23 이 이 검사를 넓혔다 — **실기에서 막혔다** ─────────
 *
 * v21 은 **`http`·`https` 만** 참으로 뒀다. 「우리가 새 탭으로 열어 줄 것은 웹 주소
 * 뿐」이라는 이유였고, 광고 소재의 딥링크가 마침 전부 웹 주소(`https://naver.me/…`)라
 * **그 전제가 한 번도 시험받지 않았다.**
 *
 * v23 의 제휴 상품은 다르다. 딥링크는 **앱 스킴**(`bloomshop://products/…`)이고, 그것이
 * 「폰이 열 곳」으로 봉투에 실려 사본까지 그대로 간다(`docs/47` §3.5). 그런데 이 검사가
 * 그것을 거부해서, 폰의 상품 화면이 **「판매처 주소를 확인하지 못했습니다」**로 멈췄다 —
 * 2026-08-27 실기. **사본에도 응답에도 딥링크는 멀쩡히 있었고**, 막은 것은 이 한 줄이다.
 *
 * **그래서 가르는 기준을 스킴 목록이 아니라 `//` 로 바꾼다.**
 *
 * | 값 | `//` | 뜻 |
 * |---|---|---|
 * | `https://shop…` · `bloomshop://products/1` | 있다 | **어디로 간다** — 참 |
 * | `javascript:alert(1)` · `data:…` · `mailto:a@b` | 없다 | **무엇을 실행한다/그 밖** — 거짓 |
 * | `''` · `/p/abc` · `?src=…` | — | 상대 URL, `new URL` 이 던진다 — 거짓 |
 *
 * `//` 는 **권한부(authority)가 뒤따른다**는 표시라, 「이 주소로 이동한다」와 「이
 * 문자열을 해석해 실행한다」를 정확히 가른다. 그 위에 **막을 스킴을 명시로 한 번 더**
 * 둔다 — `blob:`·`file:` 은 `//` 를 가질 수 있어서다.
 *
 * **`javascript:` 를 막는 것이 v21 이 세운 넷 중 하나였고, 그것은 그대로 남는다.**
 */

/**
 * `//` 를 가질 수 있으나 판매처일 수 없는 스킴. **`javascript:`·`data:` 는 `//` 가 없어
 * 아래 정규식에서 이미 걸리지만, 뜻을 코드에 남겨 두는 편이 낫다.**
 */
const BLOCKED_CHECKOUT_SCHEMES = new Set(['javascript:', 'data:', 'vbscript:', 'blob:', 'file:']);

export function isExternalCheckoutUrl(deepLink: string): boolean {
  try {
    // 베이스를 주지 않는다 — 상대 URL 이면 던진다. **그 던짐이 이 검사의 알맹이다.**
    const { protocol } = new URL(deepLink);
    if (BLOCKED_CHECKOUT_SCHEMES.has(protocol)) return false;
    // **스킴 뒤에 `//` 가 있어야 한다.** 위 표의 그 가름이다.
    return /^[a-z][a-z0-9+.-]*:\/\//i.test(deepLink.trim());
  } catch {
    return false;
  }
}

/** 테스트가 브라우저 없이 이 경로를 지나갈 수 있게 하는 최소 표면. */
export interface CheckoutWindow {
  /** 새 탭. 팝업이 막히면 `null` 을 준다 */
  open(url: string, target: string): unknown;
  /**
   * **진짜 앵커를 만들어 누른다.** 설치된 앱에서 `window.open` 이 막혀도 이쪽은
   * 브라우저가 외부로 열어 주고 **앱이 남는다** — 그것이 이 통로의 존재 이유 전부다.
   *
   * @returns 누를 수 있었는가. 열렸는지는 **알 수 없다**(반환값이 그것을 뜻하지 않는다).
   */
  openViaAnchor(url: string): boolean;
  /** 같은 탭 이동. 이 호출 뒤에는 페이지가 살아 있지 않다 */
  assign(url: string): void;
}

/**
 * 판매처로 나간다.
 *
 * @returns 새 탭으로 열렸는지, 같은 탭으로 떠났는지. 후자면 호출부의 다음 줄은
 *   대개 실행되지 않는다 — 반환값에 기대는 화면 로직을 두지 말 것.
 */
export async function openExternalCheckout(
  deps: CheckoutDeps,
  target: CheckoutTarget,
  win: CheckoutWindow = defaultWindow(),
): Promise<CheckoutExit> {
  const url = buildCheckoutUrl(target);

  /*
   * ★ **여는 것이 기록보다도 먼저다** (v21/F13, 실기가 두 번 뒤집었다).
   *
   * ── 첫 번째 뒤집힘: `await` 가 제스처를 먹었다 ──
   *
   * 전에는 `external_checkout_opened` 를 내고 **보낼 때까지 기다린 뒤에** 열었다. 근거는
   * 「떠난 뒤에는 보낼 기회가 없다」였고 상한 1.5초가 일시적 사용자 활성화 안이라고 보았다.
   * 홈 화면 앱에서는 그렇지 않았다.
   *
   * ── 두 번째 뒤집힘: 활성화가 아니라 **팝업 정책**이었다 ──
   *
   * 순서를 고쳐도 아이폰·안드로이드 **둘 다** 같은 탭으로 갔다. 설치된 앱에서는
   * `window.open` 이 새 창을 **주지 않는다.** 그래서 같은 탭 이동이 정상 경로가 되고,
   * 그것이 **방송이 있던 앱을 대체**해 돌아오면 진입 게이트였다(서버 로그에 이탈 클릭과
   * **같은 초의 부팅**이 남았다).
   *
   * 그 자리에 **진짜 앵커 클릭**을 넣는다. 브라우저가 외부로 열어 주고 **앱이 남는다.**
   *
   * ── 세 번째 뒤집힘: **`window.open` 의 반환값이 아무것도 뜻하지 않았다** ──
   *
   * 앵커를 폴백으로 두었더니 **닿지도 않았다.** `params.exit` 이 세 회차 모두 `new_tab`
   * 이었고 — 즉 `window.open` 이 창을 **돌려줬고** — 그런데 **같은 초에 앱이 다시 떴다**
   * (서버 로그의 `채널 목록 요청`). 설치된 앱에서 그 호출은 창을 주는 척하면서 **현재
   * 화면을 대체한다.**
   *
   * **그래서 앵커가 먼저다.** `window.open` 은 그 뒤의 폴백이고, 셸 브리지만 그보다
   * 앞선다(브리지는 `openViaAnchor` 가 `false` 를 주므로 자연히 그렇게 된다).
   *
   * **플랫폼으로 가르지 않는다.** 우리가 알고 싶은 것은 「설치되었는가」가 아니라
   * 「이 호출이 진짜 별 창을 주는가」이고 그것은 **알 수 없다** — `docs/39` §12.4 가
   * `display-mode: standalone` 으로 가르지 말라고 적은 이유와 같다. 알 수 없으면
   * **모두에게 같은 길**을 쓴다. 데스크톱에서도 앵커는 새 탭을 연다 — 잃는 것은
   * 「열렸다고 말할 자격」뿐이고, 그것을 문구가 이미 감당한다.
   *
   * ── 순서 ──
   *
   * 1. 연다 (앵커 → `window.open`).  **제스처 안이다**
   * 2. 무엇으로 열었는지를 **기록에 싣는다** (`params.exit`)
   * 3. 보낸다. 같은 탭으로 가야 할 때만 **떠나기 전에** 기다린다
   *
   * `noopener` 를 `window.open` **옵션으로** 주지 않는다 — 주면 언제나 `null` 이라
   * 팝업 차단과 구분할 수 없다. 앵커의 `rel="noopener"` 는 그런 부작용이 없다.
   */
  const exit: CheckoutExit = win.openViaAnchor(url)
    ? 'handed_off'
    : win.open(url, '_blank') != null
      ? 'new_tab'
      : 'same_tab';

  /*
   * **무엇으로 나갔는지를 기록한다.** 이 값이 없어서 실기 증상 하나를 로그와 추측으로만
   * 좁혔다 — 「새 탭으로 열렸는가」를 서버에서 알 방법이 없었다.
   *
   * **계약을 넓히지 않는다.** `Tv2mEvent.params` 가 이미 스칼라 자유 필드이고
   * (`docs/08` §6) 서버가 Json 으로 보관한다. 전용 필드를 하나 더 만들면 같은 값이
   * 두 자리에 살고, 그중 하나가 언젠가 안 채워진다.
   */
  deps.trackEvent('external_checkout_opened', {
    productId: target.productId,
    ...(target.matchId === undefined ? {} : { matchId: target.matchId }),
    params: { exit },
  });

  if (exit !== 'same_tab') {
    // 이 페이지는 살아 있다 — 전송을 그 뒤에 해도 된다. 상한은 그대로 둔다.
    await waitForFlush(deps.flushEvents());
    return exit;
  }

  // **떠나기 전에 보낸다.** 이 경로에는 다음 기회가 없다.
  await waitForFlush(deps.flushEvents());
  win.assign(url);
  return 'same_tab';
}

/**
 * 전송을 상한까지만 기다린다. **던지지 않는다** — 이벤트 수집이 이탈을 막으면 안 된다
 * (`docs/08` §6 의 방침 그대로).
 */
async function waitForFlush(flushing: Promise<void>): Promise<void> {
  await Promise.race([
    flushing.catch(() => undefined),
    new Promise<void>((resolve) => {
      setTimeout(resolve, FLUSH_TIMEOUT_MS);
    }),
  ]);
}

/**
 * 이탈이 실제로 지나갈 통로.
 *
 * **네이티브 셸 안이면 셸이 Safari 로 연다** (v12 · `docs/29` D9). 웹 그대로 두면
 * `window.open` 이 `null` 을 주고(WKWebView 는 새 창을 만들지 않는다) 아래 폴백이
 * **정상 경로가 되어 판매처가 셸 안에서 열린다** — 그러면 BLOOM 이 그 자리에서 사라지고
 * 내비게이션 UI 가 없어 돌아올 방법도 없다.
 *
 * 셸의 `WKNavigationDelegate` 가 우리 오리진 밖의 이동을 어차피 가로채므로 안전은
 * 그쪽이 보장한다. 이 분기가 하는 일은 **`null` → 같은 탭 폴백**이라는 우회를 거치지
 * 않게 해 반환값(`new_tab`)이 실제와 맞게 두는 것이다.
 *
 * **브리지가 없으면 한 바이트도 달라지지 않는다** — 웹·PWA·Android 경로 그대로다.
 */
function defaultWindow(): CheckoutWindow {
  const native = getNativeBridge();
  if (native !== null) {
    return {
      // 셸이 받았으면 「새 탭」이다 — 이 페이지는 살아 있고 방송이 뒤에서 계속 간다.
      // **받지 못했으면 `null` 을 주고 아래 폴백으로 내려간다** — 그 이동을
      // `WKNavigationDelegate` 가 가로채 Safari 로 연다. 조용히 성공했다고 하지 않는다.
      open: (url) => (native.openExternal(url) ? true : null),
      // 셸이 답하지 않았으면 앵커를 시도하지 않는다 — `WKNavigationDelegate` 가 아래
      // `assign` 의 이동을 가로채 Safari 로 여는 것이 그쪽의 설계다 (`docs/29` D9).
      openViaAnchor: () => false,
      assign: (url) => {
        // 여기까지 왔다는 것은 브리지가 답하지 않았다는 뜻이다. 실제로 이동시켜
        // 델리게이트에게 넘긴다 — 셸이 없으면 이 줄이 원래의 같은 탭 폴백이다.
        window.location.href = url;
      },
    };
  }
  return {
    open: (url, target) => window.open(url, target),
    openViaAnchor: (url) => clickAnchor(url),
    assign: (url) => {
      window.location.href = url;
    },
  };
}

/**
 * **진짜 앵커를 만들어 누른다** (v21/F13).
 *
 * 설치된 앱(홈 화면 PWA · WebAPK)에서 `window.open` 은 새 창을 주지 않는다. 그런데
 * `target="_blank"` 앵커의 활성화는 **브라우저가 외부로 열어 주고 앱을 남긴다** — 같은 탭
 * 이동이 방송을 대체하던 자리가 이것으로 갈린다.
 *
 * **제스처 안에서 불려야 한다.** 합성 클릭은 지금 태스크의 사용자 활성화를 물려받으므로,
 * `openExternalCheckout` 이 `await` 를 지나기 **전에** 이 함수를 부르는 것이 계약이다.
 *
 * **열렸는지는 알 수 없다.** 반환값은 「누를 수 있었는가」이지 「열렸는가」가 아니다 —
 * 그래서 화면 문구가 「열었습니다」가 아니라 「나갑니다」다.
 *
 * `rel="noopener"` 는 붙여도 된다 — `window.open` 의 `noopener` **옵션**과 달리 반환값을
 * `null` 로 만드는 부작용이 없다(그 옵션을 쓰지 않는 이유가 위에 적혀 있다).
 */
function clickAnchor(url: string): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener';
    // **문서에 붙여야 활성화가 전달되는 브라우저가 있다.** 붙였다 곧 뗀다.
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    return true;
  } catch {
    return false;
  }
}
